import React, { useEffect, useRef, useState } from "react";
import {
  descriptionToText,
  isPendingApproval,
  leagueCtaState,
  stripHtml,
} from "./leagueStatus";
import { PodiumRewardsCard } from "./PodiumBadges";
import LeagueDetailRows from "./LeagueDetailRows";
import {
  assetSelectionLabel,
  durationLabel,
  leagueTypeLabel,
} from "./leagueFormat";
import CreatedByBlock from "./CreatedByBlock";
import { memberCount } from "./inviteCode";
import {
  ChevronRightIcon,
  SmallCalendarIcon,
  SmallUsersIcon,
} from "./icons";
import {
  MdOutlineGroups,
  MdOutlineLeaderboard,
  MdOutlineSchool,
  MdOutlineVerifiedUser,
} from "react-icons/md";
import LeagueTypeIcon from "./LeagueTypeIcon";
import { toast } from "react-toastify";
import LeagueStatusLine from "./LeagueStatusLine";
import LeaguePlayTopBar from "./LeaguePlayTopBar";
import { useNavigate } from "react-router-dom";
import { standaloneLeaderboardPath } from "../../../utils/leaguePath";

// A PUBLIC league, seen by someone who hasn't joined it.
//
// Deliberately not the private invite card: nothing here is a reveal. A public
// league is listed openly, so this screen isn't breaking news that it exists —
// it's a pitch, and it's laid out as one. It opens on the name rather than a
// cover image, states the shape of the competition, then argues for joining
// (badges, what's inside) and asks.
//
// Separate cards down the page, stacked. That reads on a phone and on a desktop
// column alike, which is what the design shows.

const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-gray-200 bg-white p-5 ${className}`}>
    {children}
  </div>
);

// The three facts about the competition itself, across one row.
const Stat = ({ icon, label, value }) => (
  <div className="px-2 text-center">
    <div className="flex justify-center">{icon}</div>
    <p className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-secondary">
      {label}
    </p>
    <p className="mt-0.5 text-sm font-bold text-[#0F172A] truncate">{value}</p>
  </div>
);

// What membership actually gets you. Four claims, because four fits one row on a
// phone without the labels wrapping to three lines.
//
// Client Rev 2 went through them one by one:
//   "Full leaderboard is too abstract and visually thin"  -> "a podium or ranked
//        bars icon. Something that immediately reads competition standings, not
//        analytics."                     MdOutlineLeaderboard IS ranked bars.
//   "Member profiles is recognizable, but a single person icon is slightly odd
//        for a plural concept"           -> "two person or small group outline
//        icon."                          MdOutlineGroups.
//   "Fair play is the strongest of the four" -> "shield with check. Keep this
//        concept."                       MdOutlineVerifiedUser is exactly that.
//   "Learn by doing definitely looks like it came from another icon family"
//        -> "even a clean graduation cap is fine, but it needs to match the exact
//        stroke weight and geometry of the other three."  MdOutlineSchool is the
//        cap from the SAME family, so the geometry matches by construction.
//
// "Then standardize all four to the same optical size, stroke thickness, and dark
// teal color. I would give them identical icon boxes so one never looks larger
// just because of its shape." — the box below is a fixed 40px square with the
// glyph centred at 24px, so a wide glyph and a tall one occupy the same footprint.
// One family means one stroke thickness for free; the colour is the one dark teal.
const PERKS = [
  { Icon: MdOutlineLeaderboard, label: "Full leaderboard" },
  { Icon: MdOutlineGroups, label: "Member profiles" },
  { Icon: MdOutlineVerifiedUser, label: "Fair play" },
  { Icon: MdOutlineSchool, label: "Learn by doing" },
];

// `identityInShell` / `showJoinCta` — the SAME two switches the private card
// carries, for the same reason. Client Rev 4: "The three-dot menu or the league
// left menu **league details** link screen needs to match up with the final
// landing page designs. Right now they are different. The main landing page keeps
// getting updates, but the landing page from the league details link is not."
//
// It could not keep up: League Details rendered the PRIVATE card for every league,
// so a public league's details screen was a different design from its own landing
// page and always would be, however many times the landing page was revised. It
// now renders this card — the public landing page itself — with its identity block
// dropped (LeagueShell above it already names the league) and its Join CTA dropped
// (a member has nothing left to decide). One design, two contexts.
const PublicLeagueJoinCard = ({
  game,
  joining,
  onJoin,
  disabled,
  identityInShell = false,
  showJoinCta = true,
  showShare = true,
}) => {
  // One state, read by this button, by the supporting line above it and by the
  // phone's docked JoinBlock — see leagueCtaState, which is now the single
  // definition of what a landing page's one button offers.
  //
  // The muted "Entry closed" it replaces was a dead end. Client: "I click on an
  // entry closed Live Now Public League chevron, and it went to the landing page
  // with the muted Entry Closed button. This should be going to Leaderboard
  // preview."
  const navigate = useNavigate();
  const ctaState = leagueCtaState(game);
  // "offering" is the state that makes a promise — it drives the "Free to join"
  // line and the disabled rule. A blocked Entry closed is an answer, not an offer.
  const offering = ctaState.action === "join" && !ctaState.blocked;
  const ctaLabel = offering && joining ? "Joining\u2026" : ctaState.label;

  const onCta = () => {
    if (ctaState.action === "join") return onJoin && onJoin();
    if (ctaState.action === "leaderboard") {
      return navigate(standaloneLeaderboardPath(game?.vanity_url));
    }
    return navigate(`/contest/${game?.vanity_url}`);
  };
  const [expanded, setExpanded] = useState(false);
  // Client Rev: "Show Read more only when the Description exceeds 3 lines"
  // (mobile) / "4 rendered lines" (desktop). There is no CSS test for "did
  // line-clamp actually clamp", so the paragraph is measured: on the clamped
  // element, scrollHeight > clientHeight means there is more than the clamp shows.
  // Re-measured on resize because the clamp itself changes at md (3 -> 4) and the
  // same string wraps differently at a different width.
  const [clamped, setClamped] = useState(false);
  const descRef = useRef(null);

  useEffect(() => {
    const measure = () => {
      const node = descRef.current;
      if (!node || expanded) return;
      setClamped(node.scrollHeight - node.clientHeight > 1);
    };
    measure();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [game?.description, expanded]);

  // Client Rev 1, Screen 5: while a public league is pending approval it is not
  // listed, "Join Now" is greyed out, and it cannot be shared — "do not enable
  // public sharing while Pending approval. Once approved, the landing page Share
  // icon handles sharing the league URL."
  const pending = isPendingApproval(game);

  const tagline = stripHtml(game?.tagline || "");
  // Client Rev, both breakpoints: "Preserve organizer-entered line breaks and
  // spacing in the Description." stripHtml deleted every tag and put nothing in
  // its place, so <p>a</p><p>b</p> rendered as "ab". See leagueStatus.js.
  const description = descriptionToText(game?.description || "");
  // The eyebrow above the title. leagueTypeLabel is League Play's own wording for
  // what kind of competition this is, so a cash league says CASH GAME rather than
  // being mislabelled PUBLIC LEAGUE.
  const typeLabel = (leagueTypeLabel(game) || "").toUpperCase();

  return (
    <div className="space-y-4">
      {/* One shared desktop header row for every League Play landing — see
          LeaguePlayTopBar. Dropped inside League details, where the shell owns
          the chrome, and its share control follows the same rule this card's own
          button did: not while the league is pending approval, and never for a
          private league. */}
      {!identityInShell && (
        <LeaguePlayTopBar
          game={game}
          /* Not while the league is pending approval — client Rev 1: "do not
             enable public sharing while Pending approval. Once approved, the
             landing page Share icon handles sharing the league URL." */
          showShare={showShare && !pending}
          fallback="/contest/public-leagues"
          className="mb-2"
        />
      )}

      {/* The whole identity block — eyebrow, title, share icon, tagline, CREATED
          BY and the status — is skipped when the host already carries it. That is
          League Details, where LeagueShell prints the same three things at the top
          of the page; printing them twice is what the private card was doing
          there before. */}
      {!identityInShell && (
      <div>
        {/* Client Rev, BOTH breakpoints: "Add PUBLIC LEAGUE eyebrow above the
            dynamic league title." Same node and same classes as the eyebrow on
            League Pages (LeagueShell) and on the private landing, so the three
            screens introduce a league the same way. */}
        {typeLabel && (
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-tertiary">
            {typeLabel}
          </p>
        )}
        {/* Client Rev 2, DESKTOP: "the share icon will fit inline with the league
            title line, right-aligned, and share the same right edge as the content
            block. Even if the organizer maxes out the title, it still will not run
            into the share icon on desktop. This will allow the content block to be
            tighter to the desktop header bar."
            So the share control is a sibling of the title in one row rather than a
            row of its own above it — which is what was costing the page a whole
            block of height under the header. `min-w-0` + break-words on the title
            and `shrink-0` on the button is what guarantees the two never collide,
            however long the organiser's title is.
            MOBILE: "Remove the three-dot menu entirely… The share icon is in the
            header's right corner of all league landing pages. Keep the back arrow
            on the left… Move the whole screen content up to reclaim that dead
            space." — so on a phone this button is hidden and the app header renders
            it (see DashboardHeader's isPreJoinLanding), and the content starts at
            the eyebrow with no share row above it at all. */}
        <div className="flex items-start justify-between gap-4">
          <h1 className="min-w-0 break-words mt-0.5 font-montserrat font-bold text-title-subpage md:text-title-page text-pageTitle leading-tight">
            {game?.title}
          </h1>
          {/* The desktop share control moved OUT of the title line and up to
              the top bar, where the client asked for it: "Add the share icon
              aligned to the right edge of the content column. The arrow and
              share icon bracket the content area." Two share buttons on one
              screen would be the drift, so this one is gone rather than
              hidden. The phone's share is still the app header's, untouched. */}
        </div>
        {tagline && <p className="mt-1 text-sm md:text-base text-secondary">{tagline}</p>}

        {/* Client Rev, DESKTOP: "add eyebrow - CREATED BY + avatar + username
            block left-aligned under the tagline. Keep Starts in / Ends in
            right-aligned on the same horizontal band as the Created by block."
            And, for every screen: "Add the missing CREATED BY eyebrow above the
            organizer username… Match the approved CREATED BY treatment used
            elsewhere in League Play exactly."
            What was here was a 20px avatar and a grey handle with NO eyebrow —
            the block the client screenshotted. It is now the shared component, so
            it cannot drift from League Pages or the private landing again. The
            countdown keeps its place on the right of the same row. */}
        {/* Client Rev 3, point 2: "the status line is floating between the two
            lines in the organizer identity block. The status line should be
            aligned with the username line in the identity block." — `alignToHandle`
            is what does that; see LeagueStatusLine.
            Rev 3 also locked the COLOUR, which this screen was getting wrong in
            both states (#22A36B live / #0F4146 upcoming, against the locked
            #35B23C / #006565). It no longer chooses: LeagueStatusLine reads
            previewStatus, the single definition, and that covers the Pending
            approval pill this block used to special-case by hand too. */}
        <div className="mt-3 flex items-center justify-between gap-3">
          <CreatedByBlock game={game} />
          <LeagueStatusLine game={game} alignToHandle />
        </div>
      </div>
      )}

      <Card className="!p-0">
        <div className="grid grid-cols-3 divide-x divide-divider py-4">
          <Stat
            icon={<SmallCalendarIcon className="h-6 w-6" stroke="#0F4146" />}
            label="Duration"
            value={durationLabel(game) || "—"}
          />
          {/* ASSET TYPE wording differs by breakpoint on the client's own
              instruction — mobile "7 coins", desktop "Crypto · 7 coins". Both are
              rendered and CSS picks one, so the value can never disagree with the
              width it is shown at. See assetSelectionLabel. */}
          <Stat
            icon={<LeagueTypeIcon type="cash" className="h-6 w-6" stroke="#0F4146" />}
            label="Asset type"
            value={
              <>
                <span className="md:hidden">{assetSelectionLabel(game, "mobile")}</span>
                <span className="hidden md:inline">{assetSelectionLabel(game, "desktop")}</span>
              </>
            }
          />
          <Stat
            icon={<SmallUsersIcon className="h-6 w-6" stroke="#0F4146" />}
            label="Members"
            value={`${memberCount(game)}`}
          />
        </div>
      </Card>

      {/* Always rendered now. The guard used to be
          `description || startsAt || endsAt`, from when everything in this card
          was optional. Starting cash and Trade limit are not optional — every
          competition has both — so a league with no description and no stored
          dates would have hidden two facts the client has just asked to be
          shown. */}
      <Card>
          {description && (
            <>
              {/* Client Rev: collapsed at 3 rendered lines on MOBILE and 4 on
                  DESKTOP — the two numbers are the client's, one per breakpoint.
                  `whitespace-pre-line` is what makes the organiser's own line
                  breaks survive; without it HTML folds every newline into a space
                  and the rules run together. */}
              <p
                ref={descRef}
                /* Client Rev 2: "Right now, when you click Read more, the line
                   stays on a single line and goes off the screen. The description
                   needs to stay within the borders of the description box."
                   While clamped, line-clamp hides the overflow so nothing showed.
                   Expanded, a long unbroken run — a pasted URL, a wall of text with
                   no spaces — has nothing to break on, so it ran straight out of
                   the card. `[overflow-wrap:anywhere]` lets it break mid-token;
                   `break-words` alone does not lower min-content and would not have
                   fixed it. */
                className={`text-sm text-secondary leading-relaxed whitespace-pre-line [overflow-wrap:anywhere] ${
                  expanded ? "" : "line-clamp-3 md:line-clamp-4"
                }`}
              >
                {description}
              </p>
              {/* Was `description.length > 160` — a character count, which is not
                  what "exceeds 3 lines" means: a 200-character description of
                  short words can fit, and a 120-character one with a line break in
                  it may not. `clamped` is the measured answer. */}
              {!expanded && clamped && (
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  className="mt-2 ml-auto flex items-center gap-1 text-sm font-semibold text-[#0F4146] hover:underline"
                >
                  Read more
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              )}
            </>
          )}

          {/* Client, this revision: "Please add Starting cash and Trade limit to
              the existing league details card... The details card should contain
              six items: STARTS, ENDS, STARTING CASH, TRADE LIMIT, JOIN AFTER
              START, PORTFOLIO VISIBILITY", two columns and three rows on desktop,
              stacked on mobile.

              The six rows live in LeagueDetailRows because the PRIVATE landing
              page was given the identical block in the same round — "Match the
              public league landing page layout" — and two copies of six rows is
              two copies to keep in step. This used to be two wrapper divs holding
              a column each, which the FOUR-item version needed: its desktop order
              ran down the left column and then down the right, and a plain grid
              fills row-wise. The six-item order the client has now given IS
              row-wise, so the wrappers are gone and the grid's natural flow is
              the client's order. */}
          <LeagueDetailRows
            game={game}
            className={description ? "mt-4 border-t border-divider pt-4" : ""}
          />
      </Card>

      {/* Same three badges the create wizard promised, reframed as the reason to
          enter rather than a list of prizes. */}
      {/* Client: the public league landing page's DESKTOP subtitle only — "We
          have room for this on this particular screen." Mobile keeps the shared
          landing line, and so do the Cash Game and Private League landings, so
          this is the one screen at the one width that reads differently. It does
          narrow the earlier "all three landing pages should be identical", which
          is why it is set here rather than in PODIUM_COPY. */}
      <PodiumRewardsCard
        variantKey="public"
        surface="landing"
        subheadingDesktop="A top 3 finish adds a badge to your trophy case."
        headingColor="#1A1A1A"
      />

      <Card>
        {/* Client Rev, both breakpoints: "The 'Build your case' and 'Get inside
            the league' section header color needs to be changed to #1A1A1A" —
            which is the bodyInk token. Build your case is the PodiumRewardsCard
            above, via its headingColor prop. */}
        {/* Client Rev 2: "Section headers 'Build your case' and 'Get inside the
            league' need to be the same Monsteratt font, same size and weight. They
            need to match exactly. Color should be #1A1A1A."
            They did NOT match: this one grew to text-lg at md while Build your
            case (PodiumRewardsCard's h4) stays text-base at every width. The
            `md:text-lg` is gone, so both are Montserrat / bold / 16px / #1A1A1A on
            both breakpoints. */}
        <h2 className="font-montserrat font-bold text-base text-bodyInk">
          Get inside the league
        </h2>
        <p className="mt-0.5 text-sm text-secondary">
          Posts, charts, banter, and competition talk
        </p>

        {/* "I would give them identical icon boxes so one never looks larger just
            because of its shape" — a fixed 40px square per glyph, centred, so the
            wide group icon and the tall cap sit on the same optical footprint.
            Size comes from the box, not from each glyph's own bounding shape. */}
        <div className="mt-5 grid grid-cols-4 gap-2">
          {PERKS.map(({ Icon, label }) => (
            <div key={label} className="text-center">
              <div className="flex justify-center">
                <span className="flex h-10 w-10 items-center justify-center">
                  <Icon size={24} color="#0F4146" aria-hidden="true" />
                </span>
              </div>
              <p className="mt-2 text-[11px] font-semibold leading-tight text-[#0F172A]">
                {label}
              </p>
            </div>
          ))}
        </div>

        {/* Client Rev 2: "the icon row … feels slightly compressed vertically
            between the subtitle and Free to join. Once the new icons are
            installed, give the icon group a little more vertical breathing room,
            probably by adding a few pixels below the labels rather than making the
            whole card much taller." So the gap grows BELOW the labels only —
            mt-5 -> mt-8 here — and the card's own padding is untouched. */}
        {/* DESKTOP only. Client Rev4 (0014888 item 4) made the phone's CTA a
            sticky dock, the way the Cash Game page already does it — see
            LeagueJoinPreview. Leaving this visible on mobile too would put two
            Join League buttons on one screen, so it is `hidden md:block` and
            the dock is the phone's only CTA. The desktop treatment itself is
            untouched: the client asked for the sticky behaviour, not a new
            button. */}
        {showJoinCta && (
          <div className="hidden md:block">
            {/* Same rule as the dock's JoinBlock: the supporting line is an
                offer, so it goes when the button stops making one. */}
            {offering && (
              <p className="mt-8 text-center text-sm font-normal text-secondary">
                {game?.is_cash_prize_league ? "Entry required" : "Free to join"}
              </p>
            )}
            <button
              type="button"
              onClick={onCta}
              disabled={
                ctaState.blocked || (offering && (disabled || pending || joining))
              }
              className={`${
                offering ? "mt-2" : "mt-8"
              } flex w-full items-center justify-center gap-2 rounded-xl bg-[#0F4146] py-4 font-bold text-base text-white transition-colors hover:bg-[#0B3A3F] disabled:opacity-60`}
            >
              {ctaLabel}
              <span aria-hidden="true">→</span>
            </button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default PublicLeagueJoinCard;
